"use client";

import { PersonIcon } from "@navikt/aksel-icons";
import { PageContainer, RootPages } from "@navikt/dinesykmeldte-sidemeny";
import type { ReactElement } from "react";
import PageSideMenu from "../../../../components/PageSideMenu/PageSideMenu";
import SoknaderInfo from "../../../../components/SoknaderInfo/SoknaderInfo";
import PageError from "../../../../components/shared/errors/PageError";
import SykmeldtNotFound from "../../../../components/shared/errors/SykmeldtNotFound";
import ListSectionSkeleton from "../../../../components/shared/skeletons/ListSectionSkeleton";
import SkeletonRegion from "../../../../components/shared/skeletons/SkeletonRegion";
import SoknaderList from "../../../../components/soknader/SoknaderList";
import {
  createSoknaderBreadcrumbs,
  useUpdateBreadcrumbs,
} from "../../../../hooks/useBreadcrumbs";
import useFocusRefetch from "../../../../hooks/useFocusRefetch";
import { useSykmeldt } from "../../../../hooks/useSykmeldt";
import { fnrText, formatNameSubjective } from "../../../../utils/sykmeldtUtils";

function SoknaderPage(): ReactElement {
  const { sykmeldtId, sykmeldt, isLoading, error, sykmeldtNotFound, refetch } =
    useSykmeldt();
  const sykmeldtName = formatNameSubjective(sykmeldt?.navn);

  useFocusRefetch(refetch);
  useUpdateBreadcrumbs(
    () => createSoknaderBreadcrumbs(sykmeldtId, sykmeldt?.navn),
    [sykmeldt?.navn, sykmeldtId],
  );

  return (
    <PageContainer
      header={{
        Icon: PersonIcon,
        title: `Søknader for ${sykmeldtName}`,
        subtitle: sykmeldt && fnrText(sykmeldt.fnr),
        subtitleSkeleton: !error && !sykmeldtNotFound,
      }}
      sykmeldt={sykmeldt}
      navigation={
        !sykmeldtNotFound && (
          <PageSideMenu sykmeldt={sykmeldt} activePage={RootPages.Soknader} />
        )
      }
    >
      {isLoading && !sykmeldt && (
        <SkeletonRegion loadingText="Laster søknader">
          <ListSectionSkeleton />
        </SkeletonRegion>
      )}
      {sykmeldt && <SoknaderList sykmeldtId={sykmeldtId} sykmeldt={sykmeldt} />}
      {error && !sykmeldtNotFound && (
        <PageError
          text="Vi klarte ikke å laste søknadene"
          cause={error.message}
        />
      )}
      {sykmeldtNotFound && !error && <SykmeldtNotFound />}
      <SoknaderInfo />
    </PageContainer>
  );
}

export default SoknaderPage;
